/**
 * ReferralBanner — slim home-page nudge for active referrers.
 *
 * • Renders only when the backend says `show: true` (i.e. the customer has at
 *   least one linked shop OR has earned today). Stays mute for unauth users
 *   and inactive referrers.
 * • Two voices:
 *     – If `earned_today > 0`: celebrate ("You earned ₹X today").
 *     – Else: gentle nudge ("N shops use your code · keep sharing").
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Share2, IndianRupee, ChevronRight } from "lucide-react";
import { api, inr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function ReferralBanner() {
    const { user } = useAuth();
    const [b, setB] = useState(null);

    useEffect(() => {
        if (!user || user.role !== "CUSTOMER") return;
        api.get("/customer/referral/banner")
            .then(({ data }) => setB(data?.show ? data : null))
            .catch(() => setB(null));
    }, [user]);

    if (!b) return null;

    const celebratory = b.earned_today > 0;
    return (
        <Link
            to={b.share_url || "/refer"}
            data-testid="referral-banner"
            className="mx-4 mt-3 block rounded-2xl overflow-hidden active:scale-[0.99] transition shadow-sm"
            style={{
                background: celebratory
                    ? "linear-gradient(120deg,#16A34A 0%,#10B981 55%,#06B6D4 100%)"
                    : "linear-gradient(120deg,#7C5CFF 0%,#3B82F6 55%,#06B6D4 100%)",
            }}
        >
            <div className="px-4 py-3 text-white flex items-center gap-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                    {celebratory ? <IndianRupee size={20} /> : <Sparkles size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                    {celebratory ? (
                        <>
                            <p data-testid="referral-banner-title" className="text-sm font-extrabold leading-tight">
                                You earned {inr(b.earned_today)} today from {b.orders_today} referral order{b.orders_today === 1 ? "" : "s"}
                            </p>
                            <p className="text-[11px] opacity-90 mt-0.5 truncate">
                                Tap to see your shops · share <span className="font-mono font-bold">{b.code}</span>
                            </p>
                        </>
                    ) : (
                        <>
                            <p data-testid="referral-banner-title" className="text-sm font-extrabold leading-tight">
                                {b.shops_linked} shop{b.shops_linked === 1 ? "" : "s"} use your code
                            </p>
                            <p className="text-[11px] opacity-90 mt-0.5 truncate">
                                Share <span className="font-mono font-bold">{b.code}</span> to earn on every order
                            </p>
                        </>
                    )}
                </div>
                <div className="shrink-0 inline-flex items-center gap-1 text-xs font-bold bg-white/15 rounded-full px-3 py-1.5">
                    <Share2 size={13} /> Share
                </div>
                <ChevronRight size={18} className="shrink-0 opacity-80" />
            </div>
        </Link>
    );
}
